import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserDetialsContext } from '../context/user-details';
import PageContainer from '../components/UI/PageContainer';
import InfoModal from '../components/lab-v4/InfoModal';

const LabV4 = () => {
    const { userDetails } = useUserDetialsContext();
    const navigate = useNavigate();
    const authorizedEmails = ['wyattelliott27@gmail.com', 'michael@fullfr.me'];

    const [file, setFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [extractedText, setExtractedText] = useState('');
    const [isExtractedTextOpen, setIsExtractedTextOpen] = useState(true);
    const [error, setError] = useState(null);

    const [summary, setSummary] = useState('');
    const [summary2, setSummary2] = useState('');
    const [isSummarizing, setIsSummarizing] = useState(false);
    const [summaryError, setSummaryError] = useState(null);
    const [longTemp, setLongTemp] = useState(0.5);
    const [shortTemp, setShortTemp] = useState(0.4);
    const [distillationTemp, setDistillationTemp] = useState(0.2);
    const [characterTemp, setCharacterTemp] = useState(0.2);
    const [imageTemp, setImageTemp] = useState(0.7);
    const [longMode, setLongMode] = useState('single_gpt-5.4-mini');
    const [shortMode, setShortMode] = useState('single_gpt-5.4-mini');
    const [distillationMode, setDistillationMode] = useState('single_gpt-5.4-mini');
    const [characterMode, setCharacterMode] = useState('single_gpt-5.4-mini');
    const [imageMode, setImageMode] = useState('dall-e-3');
    const [adaptabilityMode, setAdaptabilityMode] = useState('single_gpt-5.4-mini');
    const [coverageType, setCoverageType] = useState('SCRIPT');

    // When switching to Book coverage, default all models to gpt-5.4.
    // When switching back to Script, revert to gpt-5.4-mini.
    useEffect(() => {
        const bookDefault = 'single_gpt-5.4';
        const scriptDefault = 'single_gpt-5.4-mini';
        const target = coverageType === 'BOOK' ? bookDefault : scriptDefault;
        setLongMode(target);
        setShortMode(target);
        setDistillationMode(target);
        setCharacterMode(target);
        setAdaptabilityMode(target);
    }, [coverageType]);
    const [longPrompt, setLongPrompt] = useState('');
    const [shortPrompt, setShortPrompt] = useState('');
    const [distillationPrompt, setDistillationPrompt] = useState('');
    const [characterPrompt, setCharacterPrompt] = useState('');
    const [imagePrompt, setImagePrompt] = useState('');

    const [jobId, setJobId] = useState(null);
    const [jobStatus, setJobStatus] = useState(null);
    const [chunks, setChunks] = useState([]);
    const [totalChunks, setTotalChunks] = useState(0);
    const [finalSynopsis, setFinalSynopsis] = useState(null);
    const [finalMeta, setFinalMeta] = useState(null); // { tokens, cost, model, attempts, attempt_log }
    const [shortSynopsis, setShortSynopsis] = useState('');
    const [shortMeta, setShortMeta] = useState(null); // { tokens, cost, model, attempts, attempt_log }

    const [metaLogline, setMetaLogline] = useState('');
    const [metaAdaptability, setMetaAdaptability] = useState('');
    const [metaDemographics, setMetaDemographics] = useState({});
    const [metaScriptInfo, setMetaScriptInfo] = useState({});
    const [metaComps, setMetaComps] = useState({});
    const [metaCharacters, setMetaCharacters] = useState([]);
    const [distillationMeta, setDistillationMeta] = useState(null);
    const [characterMeta, setCharacterMeta] = useState(null);
    const [imageMeta, setImageMeta] = useState(null);
    const [adaptabilityMeta, setAdaptabilityMeta] = useState(null);

    const [adaptabilityTemp, setAdaptabilityTemp] = useState(0.4);
    const [adaptabilityPrompt, setAdaptabilityPrompt] = useState('');
    const [bookLongPrompt, setBookLongPrompt] = useState('');
    const [bookShortPrompt, setBookShortPrompt] = useState('');
    const [bookDistillationPrompt, setBookDistillationPrompt] = useState('');
    const [bookCharacterPrompt, setBookCharacterPrompt] = useState('');
    const [bookImagePrompt, setBookImagePrompt] = useState('');
    const [bookAdaptabilityPrompt, setBookAdaptabilityPrompt] = useState('');
    
    const [isFetchingDefaults, setIsFetchingDefaults] = useState(true);

    const [deepDiveLoading, setDeepDiveLoading] = useState(false);
    const [deepDiveCharIndex, setDeepDiveCharIndex] = useState(null);

    const [recentJobs, setRecentJobs] = useState([]);
    const [isRecentOpen, setIsRecentOpen] = useState(false);
    const resultsRef = React.useRef(null);

    useEffect(() => {
        const fetchDefaults = async () => {
            if (!userDetails?.tokenId) return;
            try {
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}v4/summarize/defaults/`, {
                    headers: {
                        'Authorization': `Bearer ${userDetails.tokenId}`
                    }
                });
                if (response.ok) {
                    const data = await response.json();
                    if (data.success) {
                        setLongPrompt(data.script.long_prompt);
                        setShortPrompt(data.script.short_prompt);
                        setDistillationPrompt(data.script.distillation_prompt);
                        setCharacterPrompt(data.script.character_prompt);
                        setAdaptabilityPrompt(data.script.adaptability_prompt);
                        setImagePrompt(data.script.image_prompt);

                        setBookLongPrompt(data.book.long_prompt);
                        setBookShortPrompt(data.book.short_prompt);
                        setBookDistillationPrompt(data.book.distillation_prompt);
                        setBookCharacterPrompt(data.book.character_prompt);
                        setBookAdaptabilityPrompt(data.book.adaptability_prompt);
                        setBookImagePrompt(data.book.image_prompt);
                    }
                }
            } catch (err) {
                console.error("Failed to fetch defaults:", err);
            } finally {
                setIsFetchingDefaults(false);
            }
        };
        fetchDefaults();
    }, [userDetails?.tokenId]);

    const [extensionEnabled, setExtensionEnabled] = useState(true);
    const [isInfoOpen, setIsInfoOpen] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    // Best-effort note is parsed from final_synopsis when it starts with "Best-effort:"
    const finalSynopsisNote = React.useMemo(() => {
        if (!finalSynopsis || !finalSynopsis.startsWith('Best-effort:')) return null;
        const idx = finalSynopsis.indexOf('\n\n');
        return idx > 0 ? finalSynopsis.slice(0, idx).trim() : finalSynopsis.trim();
    }, [finalSynopsis]);
    const finalSynopsisBody = React.useMemo(() => {
        if (!finalSynopsis || !finalSynopsis.startsWith('Best-effort:')) return finalSynopsis;
        const idx = finalSynopsis.indexOf('\n\n');
        return idx > 0 ? finalSynopsis.slice(idx + 2).trim() : '';
    }, [finalSynopsis]);

    const activeLongPrompt = coverageType === 'BOOK' ? bookLongPrompt : longPrompt;
    const activeShortPrompt = coverageType === 'BOOK' ? bookShortPrompt : shortPrompt;
    const activeDistillationPrompt = coverageType === 'BOOK' ? bookDistillationPrompt : distillationPrompt;
    const activeCharacterPrompt = coverageType === 'BOOK' ? bookCharacterPrompt : characterPrompt;
    const activeImagePrompt = coverageType === 'BOOK' ? bookImagePrompt : imagePrompt;
    const activeAdaptabilityPrompt = coverageType === 'BOOK' ? bookAdaptabilityPrompt : adaptabilityPrompt;

    const allChunksTerminal = chunks.length > 0 && chunks.every(c => (c.status === 'COMPLETED' || c.status === 'FAILED'));
    const finalSynopsisStatus = (() => {
        // If the job failed, treat the final synopsis as failed unless we already have it.
        if (finalSynopsis && finalSynopsis.trim().length > 0) return 'COMPLETED';
        if (!jobId) return null;
        if (jobStatus === 'FAILED') return 'FAILED';
        // If chunks are still being produced, final synopsis hasn't started yet.
        if (!allChunksTerminal) return 'PENDING';
        // Chunks are done, job still processing => final synopsis in progress.
        if (jobStatus === 'PENDING' || jobStatus === 'PROCESSING') return 'PROCESSING';
        // Edge case: job completed but synopsis missing.
        if (jobStatus === 'COMPLETED') return 'FAILED';
        return 'PENDING';
    })();

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
        setExtractedText('');
        setError(null);
        setJobId(null);
        setJobStatus(null);
        setChunks([]);
        setTotalChunks(0);
        setFinalSynopsis(null);
        setSummaryError(null);
    };

    const handleUpload = async () => {
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}v4/extract/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${userDetails?.tokenId}`
                },
                body: formData,
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Upload failed');
            }

            setExtractedText(data.text);
            
            // Automatically trigger summarize after successful extraction
            await handleSummarize(data.text, file?.name);
            
        } catch (e) {
            console.error("Pipeline Error:", e);
            setError(e.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSummarize = async (textToSummarize, uploadedFilename) => {
        if (!textToSummarize) return;

        const promptFields = [
            ['Long synopsis prompt', activeLongPrompt],
            ['Short synopsis prompt', activeShortPrompt],
            ['Meta distillation prompt', activeDistillationPrompt],
            ['Character extraction prompt', activeCharacterPrompt],
            ['Character image prompt', activeImagePrompt],
            ['Adaptability prompt', activeAdaptabilityPrompt],
        ];
        const missing = promptFields.filter(([, v]) => !v || !String(v).trim()).map(([n]) => n);
        if (missing.length) {
            setSummaryError(`Fill every prompt before running. Missing or empty: ${missing.join(', ')}.`);
            return;
        }

        setIsSummarizing(true);
        setSummaryError(null);
        setJobId(null);
        setJobStatus('PENDING');
        setChunks([]);
        setFinalSynopsis(null);

        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}v4/summarize/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${userDetails?.tokenId}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    text: textToSummarize, 
                    filename: uploadedFilename || '',
                    coverage_type: coverageType,
                    long_mode: longMode, 
                    short_mode: shortMode, 
                    distillation_mode: distillationMode,
                    character_mode: characterMode,
                    image_mode: imageMode,
                    adaptability_mode: adaptabilityMode,
                    long_prompt: activeLongPrompt, 
                    short_prompt: activeShortPrompt,
                    distillation_prompt: activeDistillationPrompt,
                    character_prompt: activeCharacterPrompt,
                    image_prompt: activeImagePrompt,
                    adaptability_prompt: activeAdaptabilityPrompt,
                    long_temperature: longTemp,
                    short_temperature: shortTemp,
                    distillation_temperature: distillationTemp,
                    character_temperature: characterTemp,
                    image_temperature: imageTemp,
                    adaptability_temperature: adaptabilityTemp,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Summarization failed');
            }

            setJobId(data.job_id);
            setTotalChunks(data.total_chunks);
        } catch (e) {
            console.error("Summarize Error:", e);
            setSummaryError(e.message);
            setIsSummarizing(false);
        }
    };

    const fetchJobStatus = React.useCallback(async (overrideJobId) => {
        const activeId = overrideJobId || jobId;
        if (!activeId || !userDetails?.tokenId) return;
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}v4/summarize/status/${activeId}/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${userDetails?.tokenId}`
                }
            });
            const data = await response.json();

            if (response.ok) {
                setJobStatus(data.job_status);
                setChunks(data.chunks || []);
                if (data.final_synopsis) {
                    setFinalSynopsis(data.final_synopsis);
                }
                if (data.final_model || data.final_tokens || data.final_cost) {
                    setFinalMeta({
                        tokens: data.final_tokens || 0,
                        cost: data.final_cost || 0.0,
                        model: data.final_model || null,
                        attempts: data.final_attempts || 0,
                        attempt_log: data.final_attempt_log || [],
                    });
                }
                if (data.short_synopsis) {
                    setShortSynopsis(data.short_synopsis);
                }
                if (data.short_model || data.short_tokens || data.short_cost) {
                    setShortMeta({
                        tokens: data.short_tokens || 0,
                        cost: data.short_cost || 0.0,
                        model: data.short_model || null,
                        attempts: data.short_attempts || 0,
                        attempt_log: data.short_attempt_log || [],
                    });
                }
                
                if (data.meta_logline) setMetaLogline(data.meta_logline);
                if (data.meta_adaptability) setMetaAdaptability(data.meta_adaptability);
                if (data.meta_demographics) setMetaDemographics(data.meta_demographics);
                if (data.meta_script_info) setMetaScriptInfo(data.meta_script_info);
                if (data.meta_comps) setMetaComps(data.meta_comps);
                if (data.meta_characters) setMetaCharacters(data.meta_characters);
                
                if (data.distillation_model || data.distillation_cost) {
                    setDistillationMeta({
                        cost: data.distillation_cost || 0.0,
                        model: data.distillation_model || null
                    });
                }
                if (data.character_model || data.character_cost) {
                    setCharacterMeta({
                        cost: data.character_cost || 0.0,
                        model: data.character_model || null
                    });
                }
                if (data.image_model || data.image_cost) {
                    setImageMeta({
                        cost: data.image_cost || 0.0,
                        model: data.image_model || null
                    });
                }
                if (data.adaptability_model || data.adaptability_cost) {
                    setAdaptabilityMeta({
                        cost: data.adaptability_cost || 0.0,
                        model: data.adaptability_model || null
                    });
                }

                if (data.job_status === 'COMPLETED' || data.job_status === 'FAILED') {
                    setIsSummarizing(false);
                    if (data.job_status === 'FAILED') {
                        setSummaryError(data.error_message || 'Background processing failed.');
                    }
                    return true; // signal to stop polling
                }
            }
        } catch (e) {
            console.error("Polling error:", e);
        }
        return false;
    }, [jobId, userDetails?.tokenId]);

    React.useEffect(() => {
        let intervalId;
        if (jobId && (jobStatus === 'PENDING' || jobStatus === 'PROCESSING')) {
            const poll = async () => {
                const done = await fetchJobStatus();
                if (done && intervalId) clearInterval(intervalId);
            };
            poll(); // immediate first poll
            intervalId = setInterval(poll, 15000);
        }
        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [jobId, jobStatus, userDetails, fetchJobStatus]);

    const fetchRecentJobs = React.useCallback(async () => {
        if (!userDetails?.tokenId) return;
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}v4/summarize/recent/?limit=5`, {
                method: 'GET',
                headers: { Authorization: `Bearer ${userDetails.tokenId}` },
            });
            const data = await response.json();
            if (response.ok && data.success) {
                setRecentJobs(data.jobs || []);
            }
        } catch (e) {
            console.error('Failed to fetch recent jobs:', e);
        }
    }, [userDetails?.tokenId]);

    React.useEffect(() => {
        fetchRecentJobs();
    }, [fetchRecentJobs]);

    // Refresh the recent list once a job reaches a terminal state (title/status update).
    React.useEffect(() => {
        if (jobStatus === 'COMPLETED' || jobStatus === 'FAILED') {
            fetchRecentJobs();
        }
    }, [jobStatus, fetchRecentJobs]);

    const loadRecentJob = async (id) => {
        if (!id) return;
        setIsRecentOpen(false);
        // Reset prior run state so the loaded job renders cleanly.
        setChunks([]);
        setFinalSynopsis(null);
        setShortSynopsis('');
        setMetaLogline('');
        setMetaAdaptability('');
        setMetaDemographics({});
        setMetaScriptInfo({});
        setMetaComps({});
        setMetaCharacters([]);
        setSummaryError(null);
        setJobId(id);
        setJobStatus('PROCESSING'); // kick the poller; it self-corrects to COMPLETED/FAILED
        await fetchJobStatus(id);
        setTimeout(() => {
            resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
    };

    const startCharacterDeepDive = async (characterIndex) => {
        if (!jobId || !userDetails?.tokenId) return;
        setDeepDiveCharIndex(characterIndex);
        setDeepDiveLoading(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}v4/character/deep-dive/`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${userDetails.tokenId}`,
                    },
                    body: JSON.stringify({
                        job_id: jobId,
                        character_index: characterIndex,
                    }),
                }
            );
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Failed to start deep dive');
            }
            navigate(`/lab-v4/character-deep-dive/${data.deep_dive_id}`);
        } catch (e) {
            console.error('Deep dive start error:', e);
            setSummaryError(`Deep dive failed: ${e.message}`);
        } finally {
            setDeepDiveLoading(false);
            setDeepDiveCharIndex(null);
        }
    };

    const handleCopyAll = () => {
        let text = "--- OBSERVABILITY LOGS ---\n\n";
        chunks.forEach((c, i) => {
            text += `Chunk ${i + 1} [${c.status}]:\n`;
            text += `Tokens: ${c.tokens || 0} | Cost: $${(c.cost || 0).toFixed(4)}\n\n`;
            text += `Prompt Input:\n${c.prompt_input || 'N/A'}\n\n`;
            text += `Generated Output:\n${c.text_result || 'N/A'}\n\n`;
            if (c.error_log) text += `Error Trace:\n${c.error_log}\n\n`;
            text += '------------------------\n\n';
        });
        if (finalSynopsisStatus) {
            text += `Final Synopsis [${finalSynopsisStatus}]:\n`;
            if (jobStatus === 'FAILED' && summaryError) {
                text += `Error: ${summaryError}\n\n`;
            } else {
                text += `\n`;
            }
            text += '------------------------\n\n';
        }
        if (finalSynopsis) {
            text += `--- FINAL SYNOPSIS ---\n\n${finalSynopsis}\n`;
        }
        navigator.clipboard.writeText(text);
        alert("Copied all logs and synopsis!");
    };

    const handleCopyChunks = () => {
        let text = "--- OBSERVABILITY LOGS ---\n\n";
        chunks.forEach((c, i) => {
            text += `Chunk ${i + 1} [${c.status}]:\n`;
            text += `Tokens: ${c.tokens || 0} | Cost: $${(c.cost || 0).toFixed(4)}\n\n`;
            text += `Prompt Input:\n${c.prompt_input || 'N/A'}\n\n`;
            text += `Generated Output:\n${c.text_result || 'N/A'}\n\n`;
            if (c.error_log) text += `Error Trace:\n${c.error_log}\n\n`;
            text += '------------------------\n\n';
        });
        navigator.clipboard.writeText(text);
        alert("Copied all chunks!");
    };

    const handleCopySynopsis = () => {
        if (!finalSynopsis) return;
        navigator.clipboard.writeText(finalSynopsis);
        alert("Copied final synopsis!");
    };

    const handleDownload = async () => {
        if (!jobId || isDownloading) return;
        setIsDownloading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}v4/summarize/download/${jobId}/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${userDetails?.tokenId}`
                }
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.error || `Download failed (${response.status})`);
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            let filename = `Summary_Report_${String(jobId).slice(0, 8)}.docx`;
            if (metaScriptInfo?.Title && metaScriptInfo.Title.toUpperCase() !== 'N/A' && metaScriptInfo.Title.toUpperCase() !== 'UNKNOWN') {
                const safeTitle = metaScriptInfo.Title.replace(/[\\/*?:"<>|]/g, '').trim();
                if (safeTitle) filename = `${safeTitle}.docx`;
            }
            link.setAttribute('download', filename);
            
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            alert(`Download failed: ${e.message}`);
        } finally {
            setIsDownloading(false);
        }
    };

    // Loading State
    if (!userDetails || !userDetails.email) {
        return (
            <PageContainer>
                <div className="flex flex-col items-center justify-center h-[50vh] text-white">
                    <h1 className="text-2xl font-bold mb-4">Verifying Access...</h1>
                    <p>Current Email: {userDetails?.email || 'Loading...'}</p>
                </div>
            </PageContainer>
        );
    }

    if (!authorizedEmails.includes(userDetails.email)) {
        return (
            <PageContainer>
                <div className="flex flex-col items-center justify-center h-[50vh] text-white">
                    <h1 className="text-4xl font-bold mb-4">404 - Page Not Found (Access Denied)</h1>
                    <p>The page you are looking for does not exist for user: {userDetails.email}</p>
                </div>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <div className="flex flex-col text-white w-full max-w-[90rem] mx-auto px-6 py-12 md:py-16 space-y-16">
                <div className="text-center w-full space-y-4">
                    <div className="flex flex-col items-center justify-center gap-4">
                        <h1 className="text-5xl md:text-6xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-500 flex items-center gap-4 tracking-tight">
                            Script Analysis Lab V4
                            <button 
                                onClick={() => setIsInfoOpen(true)}
                                className="text-gray-400 hover:text-blue-400 transition-colors focus:outline-none"
                                title="View Model Info & Pricing"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </button>
                        </h1>
                        <label className="flex items-center gap-2 cursor-pointer select-none" title={extensionEnabled ? 'Extension active' : 'Extension paused'}>
                            <span className="text-sm text-gray-400">{extensionEnabled ? 'ON' : 'OFF'}</span>
                            <div
                                role="switch"
                                aria-checked={extensionEnabled}
                                tabIndex={0}
                                onClick={() => setExtensionEnabled(v => !v)}
                                onKeyDown={(e) => e.key === 'Enter' && setExtensionEnabled(v => !v)}
                                className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${extensionEnabled ? 'bg-blue-600' : 'bg-gray-600'}`}
                            >
                                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all duration-200 ${extensionEnabled ? 'left-[28px]' : 'left-1'}`} />
                            </div>
                        </label>
                    </div>
                    <p className="text-gray-400/80 max-w-2xl mx-auto text-lg leading-relaxed font-light tracking-wide">
                        Extract text from PDF documents and analyze with the multi-model pipeline. {!extensionEnabled && <span className="text-amber-400/90 font-medium">(Extension paused)</span>}
                    </p>
                </div>
                
                    {/* Upload Section */}
                    <div className="border border-white/5 p-8 md:p-10 rounded-[2rem] bg-gray-900/40 backdrop-blur-xl shadow-2xl shadow-black/50">
                        <div className="flex items-center gap-2 mb-6">
                            <button
                                onClick={() => setCoverageType('SCRIPT')}
                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                                    coverageType === 'SCRIPT'
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                }`}
                                disabled={!extensionEnabled || isLoading || isSummarizing}
                            >
                                Script
                            </button>
                            <button
                                onClick={() => setCoverageType('BOOK')}
                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                                    coverageType === 'BOOK'
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                }`}
                                disabled={!extensionEnabled || isLoading || isSummarizing}
                            >
                                Book
                            </button>

                            <div className="relative ml-auto">
                                <button
                                    type="button"
                                    onClick={() => { setIsRecentOpen((o) => !o); if (!isRecentOpen) fetchRecentJobs(); }}
                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700 transition-colors"
                                >
                                    Recent runs ▾
                                </button>
                                {isRecentOpen && (
                                    <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-40">
                                        {recentJobs.length === 0 ? (
                                            <p className="p-4 text-xs text-gray-500">No recent runs.</p>
                                        ) : (
                                            recentJobs.map((rj) => (
                                                <button
                                                    key={rj.job_id}
                                                    type="button"
                                                    onClick={() => loadRecentJob(rj.job_id)}
                                                    className="w-full text-left px-4 py-3 hover:bg-gray-800 border-b border-gray-800 last:border-0 transition-colors"
                                                >
                                                    <div className="flex items-center justify-between gap-2">
                                                        <span className="text-sm font-semibold text-gray-100 truncate">{rj.title}</span>
                                                        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded flex-shrink-0 ${
                                                            rj.status === 'COMPLETED' ? 'bg-green-900/50 text-green-300'
                                                            : rj.status === 'FAILED' ? 'bg-red-900/50 text-red-300'
                                                            : 'bg-yellow-900/50 text-yellow-300'
                                                        }`}>
                                                            {rj.status}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span className="text-[10px] font-mono text-gray-500 uppercase">{rj.coverage_type}</span>
                                                        {rj.created_at && (
                                                            <span className="text-[10px] text-gray-600">
                                                                {new Date(rj.created_at).toLocaleString()}
                                                            </span>
                                                        )}
                                                    </div>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* Top row: file picker + generate button */}
                        <div className="flex flex-col sm:flex-row items-center gap-6 mb-10">
                            <div className="flex-1 w-full">
                                <h2 className="text-base font-semibold text-gray-200 mb-1">
                                    {coverageType === 'BOOK' ? 'Upload book manuscript (PDF)' : 'Upload screenplay (PDF)'}
                                </h2>
                                <input
                                    type="file"
                                    accept=".pdf"
                                    onChange={handleFileChange}
                                    className="block w-full text-sm text-gray-400
                                      file:mr-4 file:py-2 file:px-4
                                      file:rounded-full file:border-0
                                      file:text-sm file:font-semibold
                                      file:bg-blue-600 file:text-white
                                      hover:file:bg-blue-700 cursor-pointer
                                    "
                                />
                            </div>
                            <button
                                onClick={handleUpload}
                                disabled={!extensionEnabled || !file || isLoading || isSummarizing}
                                className={`px-8 py-2.5 rounded-xl font-bold transition-all whitespace-nowrap text-sm ${
                                    extensionEnabled && file && !isLoading && !isSummarizing
                                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white shadow-lg shadow-purple-900/30'
                                        : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                }`}
                            >
                                {isLoading ? 'Extracting...' : isSummarizing ? 'Running...' : 'Generate Coverage'}
                            </button>
                        </div>

                        {/* Model selectors grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {/* Long Synopsis */}
                            <div className="bg-gray-950/40 rounded-2xl p-4 border-l-4 border-purple-500/80 hover:bg-gray-950/60 transition-colors">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-[10px] font-bold text-purple-400 uppercase tracking-widest">Long Synopsis</span>
                                    <span className="text-[10px] text-gray-500 uppercase tracking-wider">Temp</span>
                                </div>
                                <div className="flex gap-2">
                                    <select
                                        value={longMode}
                                        onChange={(e) => setLongMode(e.target.value)}
                                        className="flex-1 px-2 py-1.5 rounded-lg bg-gray-800 text-white border border-gray-700 outline-none focus:border-purple-500 transition-colors cursor-pointer text-xs"
                                        disabled={!extensionEnabled || isLoading || isSummarizing}
                                    >
                                        <option value="single_gpt-5-mini">GPT-5 Mini</option>
                                        <option value="single_gpt-5.1">GPT-5.1</option>
                                        <option value="single_gpt-5.3-chat">GPT-5.3 Chat</option>
                                        <option value="single_gpt-5.4">GPT-5.4</option>
                                        <option value="single_gpt-5.4-mini">GPT-5.4 Mini</option>
                                    </select>
                                    <input type="number" min="0" max="2" step="0.1" value={longTemp}
                                        onChange={(e) => setLongTemp(parseFloat(e.target.value))}
                                        className="w-14 px-2 py-1.5 rounded-lg bg-gray-800 text-white border border-gray-700 outline-none focus:border-purple-500 transition-colors text-xs text-center"
                                        disabled={!extensionEnabled || isLoading || isSummarizing}
                                    />
                                </div>
                            </div>

                            {/* Short Synopsis */}
                            <div className="bg-gray-950/40 rounded-2xl p-4 border-l-4 border-emerald-500/80 hover:bg-gray-950/60 transition-colors">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest">Short Synopsis</span>
                                    <span className="text-[10px] text-gray-500 uppercase tracking-wider">Temp</span>
                                </div>
                                <div className="flex gap-2">
                                    <select
                                        value={shortMode}
                                        onChange={(e) => setShortMode(e.target.value)}
                                        className="flex-1 px-2 py-1.5 rounded-lg bg-gray-800 text-white border border-gray-700 outline-none focus:border-emerald-500 transition-colors cursor-pointer text-xs"
                                        disabled={!extensionEnabled || isLoading || isSummarizing}
                                    >
                                        <option value="single_gpt-5-mini">GPT-5 Mini</option>
                                        <option value="single_gpt-5.1">GPT-5.1</option>
                                        <option value="single_gpt-5.3-chat">GPT-5.3 Chat</option>
                                        <option value="single_gpt-5.4">GPT-5.4</option>
                                        <option value="single_gpt-5.4-mini">GPT-5.4 Mini</option>
                                    </select>
                                    <input type="number" min="0" max="2" step="0.1" value={shortTemp}
                                        onChange={(e) => setShortTemp(parseFloat(e.target.value))}
                                        className="w-14 px-2 py-1.5 rounded-lg bg-gray-800 text-white border border-gray-700 outline-none focus:border-emerald-500 transition-colors text-xs text-center"
                                        disabled={!extensionEnabled || isLoading || isSummarizing}
                                    />
                                </div>
                            </div>

                            {/* Meta Distillation */}
                            <div className="bg-gray-950/40 rounded-2xl p-4 border-l-4 border-cyan-500/80 hover:bg-gray-950/60 transition-colors">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-[10px] font-bold text-cyan-400 uppercase tracking-widest">Meta Distillation</span>
                                    <span className="text-[10px] text-gray-500 uppercase tracking-wider">Temp</span>
                                </div>
                                <div className="flex gap-2">
                                    <select
                                        value={distillationMode}
                                        onChange={(e) => setDistillationMode(e.target.value)}
                                        className="flex-1 px-2 py-1.5 rounded-lg bg-gray-800 text-white border border-gray-700 outline-none focus:border-cyan-500 transition-colors cursor-pointer text-xs"
                                        disabled={!extensionEnabled || isLoading || isSummarizing}
                                    >
                                        <option value="single_gpt-5-mini">GPT-5 Mini</option>
                                        <option value="single_gpt-5.1">GPT-5.1</option>
                                        <option value="single_gpt-5.3-chat">GPT-5.3 Chat</option>
                                        <option value="single_gpt-5.4">GPT-5.4</option>
                                        <option value="single_gpt-5.4-mini">GPT-5.4 Mini</option>
                                    </select>
                                    <input type="number" min="0" max="2" step="0.1" value={distillationTemp}
                                        onChange={(e) => setDistillationTemp(parseFloat(e.target.value))}
                                        className="w-14 px-2 py-1.5 rounded-lg bg-gray-800 text-white border border-gray-700 outline-none focus:border-cyan-500 transition-colors text-xs text-center"
                                        disabled={!extensionEnabled || isLoading || isSummarizing}
                                    />
                                </div>
                            </div>

                            {/* Character Extraction */}
                            <div className="bg-gray-950/40 rounded-2xl p-4 border-l-4 border-amber-500/80 hover:bg-gray-950/60 transition-colors">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-[10px] font-bold text-amber-400 uppercase tracking-widest">Character</span>
                                    <span className="text-[10px] text-gray-500 uppercase tracking-wider">Temp</span>
                                </div>
                                <div className="flex gap-2">
                                    <select
                                        value={characterMode}
                                        onChange={(e) => setCharacterMode(e.target.value)}
                                        className="flex-1 px-2 py-1.5 rounded-lg bg-gray-800 text-white border border-gray-700 outline-none focus:border-amber-500 transition-colors cursor-pointer text-xs"
                                        disabled={!extensionEnabled || isLoading || isSummarizing}
                                    >
                                        <option value="single_gpt-5-mini">GPT-5 Mini</option>
                                        <option value="single_gpt-5.1">GPT-5.1</option>
                                        <option value="single_gpt-5.3-chat">GPT-5.3 Chat</option>
                                        <option value="single_gpt-5.4">GPT-5.4</option>
                                        <option value="single_gpt-5.4-mini">GPT-5.4 Mini</option>
                                    </select>
                                    <input type="number" min="0" max="2" step="0.1" value={characterTemp}
                                        onChange={(e) => setCharacterTemp(parseFloat(e.target.value))}
                                        className="w-14 px-2 py-1.5 rounded-lg bg-gray-800 text-white border border-gray-700 outline-none focus:border-amber-500 transition-colors text-xs text-center"
                                        disabled={!extensionEnabled || isLoading || isSummarizing}
                                    />
                                </div>
                            </div>

                            {/* Adaptability */}
                            <div className="bg-gray-950/40 rounded-2xl p-4 border-l-4 border-violet-500/80 hover:bg-gray-950/60 transition-colors">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-[10px] font-bold text-violet-400 uppercase tracking-widest">Adaptability</span>
                                    <span className="text-[10px] text-gray-500 uppercase tracking-wider">Temp</span>
                                </div>
                                <div className="flex gap-2">
                                    <select
                                        value={adaptabilityMode}
                                        onChange={(e) => setAdaptabilityMode(e.target.value)}
                                        className="flex-1 px-2 py-1.5 rounded-lg bg-gray-800 text-white border border-gray-700 outline-none focus:border-violet-500 transition-colors cursor-pointer text-xs"
                                        disabled={!extensionEnabled || isLoading || isSummarizing}
                                    >
                                        <option value="single_gpt-5-mini">GPT-5 Mini</option>
                                        <option value="single_gpt-5.1">GPT-5.1</option>
                                        <option value="single_gpt-5.3-chat">GPT-5.3 Chat</option>
                                        <option value="single_gpt-5.4">GPT-5.4</option>
                                        <option value="single_gpt-5.4-mini">GPT-5.4 Mini</option>
                                    </select>
                                    <input type="number" min="0" max="2" step="0.1" value={adaptabilityTemp}
                                        onChange={(e) => setAdaptabilityTemp(parseFloat(e.target.value))}
                                        className="w-14 px-2 py-1.5 rounded-lg bg-gray-800 text-white border border-gray-700 outline-none focus:border-violet-500 transition-colors text-xs text-center"
                                        disabled={!extensionEnabled || isLoading || isSummarizing}
                                    />
                                </div>
                            </div>

                            {/* Image Model */}
                            <div className="bg-gray-950/40 rounded-2xl p-4 border-l-4 border-rose-500/80 hover:bg-gray-950/60 transition-colors">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-[10px] font-bold text-rose-400 uppercase tracking-widest">Image Model</span>
                                    <span className="text-[10px] text-gray-500 uppercase tracking-wider">Temp</span>
                                </div>
                                <div className="flex gap-2">
                                    <select
                                        value={imageMode}
                                        onChange={(e) => setImageMode(e.target.value)}
                                        className="flex-1 px-2 py-1.5 rounded-lg bg-gray-800 text-white border border-gray-700 outline-none focus:border-rose-500 transition-colors cursor-pointer text-xs"
                                        disabled={!extensionEnabled || isLoading || isSummarizing}
                                    >
                                        <option value="dall-e-3">DALL-E 3</option>
                                        <option value="flux-cinestill">Flux (Cinestill)</option>
                                    </select>
                                    <input type="number" min="0" max="2" step="0.1" value={imageTemp}
                                        onChange={(e) => setImageTemp(parseFloat(e.target.value))}
                                        className="w-14 px-2 py-1.5 rounded-lg bg-gray-800 text-white border border-gray-700 outline-none focus:border-rose-500 transition-colors text-xs text-center"
                                        disabled={!extensionEnabled || isLoading || isSummarizing}
                                    />
                                </div>
                            </div>
                        </div>

                        {error && (
                            <div className="mt-4 p-4 bg-red-900/50 border border-red-500 rounded-xl text-red-200">
                                <strong>Error: </strong>{error}
                            </div>
                        )}
                    </div>


                    {/* Prompt Configuration */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div className="rounded-[2rem] bg-gray-900/40 backdrop-blur-xl border border-white/5 overflow-hidden shadow-2xl shadow-black/50">
                            <div className="h-1 bg-gradient-to-r from-purple-600 to-purple-400" />
                            <div className="p-5">
                                <h2 className="text-base font-bold mb-1 text-purple-300">Long Synopsis Prompt</h2>
                                <p className="text-xs text-gray-500 mb-3">Single source of truth sent to the model. Use <code className="text-gray-400 bg-gray-900/60 px-1 rounded">{'{text}'}</code> where the screenplay should appear.</p>
                                <textarea
                                    value={activeLongPrompt}
                                    onChange={(e) => coverageType === 'BOOK' ? setBookLongPrompt(e.target.value) : setLongPrompt(e.target.value)}
                                    className="w-full h-72 p-3 rounded-[1rem] bg-gray-950/50 text-gray-300 border border-white/5 font-mono text-xs focus:border-purple-500 focus:outline-none transition-colors resize-none shadow-inner"
                                    disabled={!extensionEnabled || isLoading || isSummarizing}
                                />
                            </div>
                        </div>
                        <div className="rounded-[2rem] bg-gray-900/40 backdrop-blur-xl border border-white/5 overflow-hidden shadow-2xl shadow-black/50">
                            <div className="h-1 bg-gradient-to-r from-emerald-600 to-emerald-400" />
                            <div className="p-5">
                                <h2 className="text-base font-bold mb-1 text-emerald-300">Short Synopsis Prompt</h2>
                                <p className="text-xs text-gray-500 mb-3">Single source of truth. Use <code className="text-gray-400 bg-gray-900/60 px-1 rounded">{'{text}'}</code> where the long synopsis should appear.</p>
                                <textarea
                                    value={activeShortPrompt}
                                    onChange={(e) => coverageType === 'BOOK' ? setBookShortPrompt(e.target.value) : setShortPrompt(e.target.value)}
                                    className="w-full h-72 p-3 rounded-[1rem] bg-gray-950/50 text-gray-300 border border-white/5 font-mono text-xs focus:border-emerald-500 focus:outline-none transition-colors resize-none shadow-inner"
                                    disabled={!extensionEnabled || isLoading || isSummarizing}
                                />
                            </div>
                        </div>
                        <div className="rounded-[2rem] bg-gray-900/40 backdrop-blur-xl border border-white/5 overflow-hidden shadow-2xl shadow-black/50">
                            <div className="h-1 bg-gradient-to-r from-cyan-600 to-cyan-400" />
                            <div className="p-5">
                                <h2 className="text-base font-bold mb-1 text-cyan-300">Meta Distillation Prompt</h2>
                                <p className="text-xs text-gray-500 mb-3">Defines logline, demographics, comps, and <code className="text-gray-400 bg-gray-900/60 px-1 rounded">cinematic_setting</code> for images. Long synopsis sent by the backend.</p>
                                <textarea
                                    value={activeDistillationPrompt}
                                    onChange={(e) => coverageType === 'BOOK' ? setBookDistillationPrompt(e.target.value) : setDistillationPrompt(e.target.value)}
                                    className="w-full h-72 p-3 rounded-[1rem] bg-gray-950/50 text-gray-300 border border-white/5 font-mono text-xs focus:border-cyan-500 focus:outline-none transition-colors resize-none shadow-inner"
                                    disabled={!extensionEnabled || isLoading || isSummarizing}
                                />
                            </div>
                        </div>
                        <div className="rounded-[2rem] bg-gray-900/40 backdrop-blur-xl border border-white/5 overflow-hidden shadow-2xl shadow-black/50">
                            <div className="h-1 bg-gradient-to-r from-amber-600 to-amber-400" />
                            <div className="p-5">
                                <h2 className="text-base font-bold mb-1 text-amber-300">Character Extraction Prompt</h2>
                                <p className="text-xs text-gray-500 mb-3">Full system prompt for character extraction. Screenplay text appended by the backend.</p>
                                <textarea
                                    value={activeCharacterPrompt}
                                    onChange={(e) => coverageType === 'BOOK' ? setBookCharacterPrompt(e.target.value) : setCharacterPrompt(e.target.value)}
                                    className="w-full h-72 p-3 rounded-[1rem] bg-gray-950/50 text-gray-300 border border-white/5 font-mono text-xs focus:border-amber-500 focus:outline-none transition-colors resize-none shadow-inner"
                                    disabled={!extensionEnabled || isLoading || isSummarizing}
                                />
                            </div>
                        </div>
                        <div className="rounded-[2rem] bg-gray-900/40 backdrop-blur-xl border border-white/5 overflow-hidden shadow-2xl shadow-black/50">
                            <div className="h-1 bg-gradient-to-r from-violet-600 to-violet-400" />
                            <div className="p-5">
                                <h2 className="text-base font-bold mb-1 text-violet-300">Adaptability Prompt</h2>
                                <p className="text-xs text-gray-500 mb-3">Runs in parallel with Long Synopsis — receives the full screenplay. Plain prose output, no JSON schema.</p>
                                <textarea
                                    value={activeAdaptabilityPrompt}
                                    onChange={(e) => coverageType === 'BOOK' ? setBookAdaptabilityPrompt(e.target.value) : setAdaptabilityPrompt(e.target.value)}
                                    className="w-full h-72 p-3 rounded-[1rem] bg-gray-950/50 text-gray-300 border border-white/5 font-mono text-xs focus:border-violet-500 focus:outline-none transition-colors resize-none shadow-inner"
                                    disabled={!extensionEnabled || isLoading || isSummarizing}
                                />
                            </div>
                        </div>
                        <div className="rounded-[2rem] bg-gray-900/40 backdrop-blur-xl border border-white/5 overflow-hidden shadow-2xl shadow-black/50">
                            <div className="h-1 bg-gradient-to-r from-rose-600 to-rose-400" />
                            <div className="p-5">
                                <h2 className="text-base font-bold mb-1 text-rose-300">Character Image Prompt Template</h2>
                                <p className="text-xs text-gray-500 mb-3">Full image prompt template. Placeholders filled by the server: <code className="text-gray-400 bg-gray-900/60 px-1 rounded">{'{name}'}</code>, <code className="text-gray-400 bg-gray-900/60 px-1 rounded">{'{demographics}'}</code>, <code className="text-gray-400 bg-gray-900/60 px-1 rounded">{'{description}'}</code>, <code className="text-gray-400 bg-gray-900/60 px-1 rounded">{'{setting_part}'}</code>.</p>
                                <textarea
                                    value={activeImagePrompt}
                                    onChange={(e) => coverageType === 'BOOK' ? setBookImagePrompt(e.target.value) : setImagePrompt(e.target.value)}
                                    className="w-full h-72 p-3 rounded-[1rem] bg-gray-950/50 text-gray-300 border border-white/5 font-mono text-xs focus:border-rose-500 focus:outline-none transition-colors resize-none shadow-inner"
                                    disabled={!extensionEnabled || isLoading || isSummarizing}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Output Section */}
                    {extractedText ? (
                        <div className="space-y-6">
                            <div 
                                className="flex items-center justify-between cursor-pointer group"
                                onClick={() => setIsExtractedTextOpen(!isExtractedTextOpen)}
                            >
                                <h3 className="text-xl font-bold text-gray-300 tracking-wide group-hover:text-white transition-colors">Extracted Text ({extractedText.length} chars)</h3>
                                <div className="text-gray-500 group-hover:text-gray-300 transition-colors">
                                    {isExtractedTextOpen ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                        </svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    )}
                                </div>
                            </div>
                            {isExtractedTextOpen && (
                                <div className="bg-gray-950/60 p-6 rounded-3xl overflow-y-auto whitespace-pre-wrap font-mono text-sm text-gray-400/80 h-48 max-h-48 border border-white/5 shadow-inner">
                                    {extractedText}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="border border-white/5 p-16 rounded-[2rem] text-center text-gray-400/80 italic bg-gray-900/40 backdrop-blur-xl shadow-2xl shadow-black/50 tracking-wide text-lg">
                            Upload a PDF to view extracted text and generate coverage.
                        </div>
                    )}

                    {/* Summary Section */}
                    {(extractedText || jobId) && (
                        <div ref={resultsRef} className="rounded-[2rem] border border-white/5 bg-gray-900/40 backdrop-blur-xl p-8 md:p-12 space-y-12 shadow-2xl shadow-black/50">
                            <h2 className="text-2xl font-bold text-gray-200 tracking-wide">Analysis Pipeline</h2>
                            
                            {summaryError && (
                                <div className="p-4 bg-red-950/80 border border-red-600 rounded-lg shadow-xl text-red-200 overflow-x-auto">
                                    <div className="font-bold text-red-400 mb-2 font-sans text-sm pb-2 border-b border-red-900/50">
                                        Pipeline Execution Error
                                    </div>
                                    <div className="whitespace-pre-wrap font-mono text-xs leading-relaxed">
                                        {summaryError}
                                    </div>
                                </div>
                            )}

                                    <div className="space-y-6 mt-6">
                                        <div className="flex flex-col sm:flex-row justify-between items-end sm:items-center gap-4">
                                            <h3 className="text-xl font-bold text-gray-200">
                                                Observability Actions
                                            </h3>
                                            {jobId && (
                                                <div className="flex flex-wrap gap-2">
                                                    <button onClick={handleCopyAll} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-xs font-semibold transition-colors text-white">
                                                        Copy All Logs & Synopsis
                                                    </button>
                                                    {finalSynopsis && (
                                                        <button onClick={handleCopySynopsis} className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 rounded text-xs font-semibold transition-colors text-white">
                                                            Copy Synopsis Only
                                                        </button>
                                                    )}
                                                    {jobStatus === 'COMPLETED' && (
                                                        <button
                                                            onClick={handleDownload}
                                                            disabled={isDownloading}
                                                            className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors text-white flex items-center gap-1.5 ${isDownloading ? 'bg-gray-600 cursor-not-allowed' : 'bg-emerald-700 hover:bg-emerald-600'}`}
                                                        >
                                                            {isDownloading ? (
                                                                <>
                                                                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
                                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                                                                    </svg>
                                                                    Generating...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                                    </svg>
                                                                    Download .docx
                                                                </>
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                            {/* Final Synopsis Status and Render */}
                            {jobId && (
                                <div className="mt-4">
                                    <div className="rounded-[2rem] bg-gray-900/40 backdrop-blur-xl border border-white/5 overflow-hidden shadow-2xl shadow-black/50 flex flex-col w-full">
                                        <div className="h-1 bg-gradient-to-r from-blue-500 to-purple-500" />
                                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 bg-gray-900/40 border-b border-gray-700/50">
                                            <div className="flex items-center gap-4 mb-2 sm:mb-0">
                                                <h3 className="font-bold text-lg text-purple-300">
                                                    Long Synopsis
                                                </h3>
                                                {finalMeta?.model && (
                                                    <span className="text-[10px] px-2 py-1 rounded bg-gray-800 text-gray-400 border border-gray-700 font-mono uppercase">
                                                        {finalMeta.model}
                                                    </span>
                                                )}
                                                {finalSynopsisStatus && (
                                                    <span
                                                        className={`text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wider ${
                                                            finalSynopsisStatus === 'COMPLETED'
                                                                ? 'bg-green-900/50 text-green-300 border border-green-700/50'
                                                                : finalSynopsisStatus === 'FAILED'
                                                                    ? 'bg-red-900/50 text-red-300 border border-red-700/50'
                                                                    : finalSynopsisStatus === 'PROCESSING'
                                                                        ? 'bg-yellow-900/50 text-yellow-300 border border-yellow-700/50 animate-pulse'
                                                                        : 'bg-gray-800 text-gray-400 border border-gray-700/50'
                                                        }`}
                                                    >
                                                        {finalSynopsisStatus}
                                                    </span>
                                                )}
                                            </div>
                                            {(finalSynopsisBody || finalSynopsis) && (
                                                <div className="flex items-center gap-3">
                                                    <span className="text-[10px] bg-gray-800 text-gray-400 px-3 py-1 rounded border border-gray-700 font-mono">
                                                        {(finalSynopsisBody || finalSynopsis).trim().split(/\s+/).length} words
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="p-6 bg-gray-900/20">
                                            {finalSynopsisNote && (
                                                <div className="mb-4 p-3 rounded-lg border border-amber-700/50 bg-amber-950/30 text-amber-200 text-sm font-mono">
                                                    {finalSynopsisNote}
                                                </div>
                                            )}
                                            {finalSynopsisStatus === 'COMPLETED' && (finalSynopsisBody || finalSynopsis) ? (
                                                <div className="whitespace-pre-wrap font-serif text-base text-gray-200 leading-relaxed max-w-4xl">
                                                    {finalSynopsisBody || finalSynopsis}
                                                </div>
                                            ) : finalSynopsisStatus === 'FAILED' ? (
                                                <div className="whitespace-pre-wrap font-mono text-xs text-red-300 bg-red-950/30 p-4 rounded-lg border border-red-900/50">
                                                    {summaryError || 'Final synopsis failed to generate.'}
                                                </div>
                                            ) : finalSynopsisStatus === 'PROCESSING' ? (
                                                <div className="text-gray-400 italic">
                                                    Script payload loaded. Generating the single-shot synopsis now...
                                                </div>
                                            ) : (
                                                <div className="text-gray-500 italic">
                                                    Waiting for script payload to stabilize...
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Short Synopsis Status and Render */}
                            {jobId && finalSynopsisStatus === 'COMPLETED' && (
                                <div>
                                    <div className="rounded-[2rem] bg-gray-900/40 backdrop-blur-xl border border-white/5 overflow-hidden shadow-2xl shadow-black/50 flex flex-col w-full">
                                        <div className="h-1 bg-gradient-to-r from-emerald-500 to-green-500" />
                                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 bg-gray-900/40 border-b border-gray-700/50">
                                            <div className="flex items-center gap-4 mb-2 sm:mb-0">
                                                <h3 className="font-bold text-lg text-emerald-300">
                                                    Short Synopsis
                                                </h3>
                                                {shortMeta?.model && (
                                                    <span className="text-[10px] px-2 py-1 rounded bg-gray-800 text-gray-400 border border-gray-700 font-mono uppercase">
                                                        {shortMeta.model}
                                                    </span>
                                                )}
                                                {jobStatus === 'COMPLETED' ? (
                                                    <span className="text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wider bg-green-900/50 text-green-300 border border-green-700/50">
                                                        COMPLETED
                                                    </span>
                                                ) : jobStatus === 'FAILED' ? (
                                                    <span className="text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wider bg-red-900/50 text-red-300 border border-red-700/50">
                                                        FAILED
                                                    </span>
                                                ) : (
                                                    <span className="text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wider bg-yellow-900/50 text-yellow-300 border border-yellow-700/50 animate-pulse">
                                                        PROCESSING
                                                    </span>
                                                )}
                                            </div>
                                            {shortSynopsis && (
                                                <div className="flex items-center gap-3">
                                                    <span className="text-[10px] bg-gray-800 text-gray-400 px-3 py-1 rounded border border-gray-700 font-mono">
                                                        {shortSynopsis.trim().split(/\s+/).length} words
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="p-6 bg-gray-900/20">
                                            {shortSynopsis && jobStatus === 'COMPLETED' ? (
                                                <div className="whitespace-pre-wrap font-serif text-base text-gray-200 leading-relaxed max-w-4xl">
                                                    {shortSynopsis}
                                                </div>
                                            ) : jobStatus === 'FAILED' && !shortSynopsis ? (
                                                <div className="whitespace-pre-wrap font-mono text-xs text-red-300 bg-red-950/30 p-4 rounded-lg border border-red-900/50">
                                                    Short synopsis failed to generate.
                                                </div>
                                            ) : (
                                                <div className="text-gray-400 italic">
                                                    Distilling Long Synopsis...
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Meta Distillation Output */}
                            {jobId && (metaLogline || metaAdaptability || metaDemographics?.age_range) && (
                                <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                                    
                                    {/* Logline */}
                                    {metaLogline && (
                                        <div className="rounded-[2rem] bg-gray-900/40 backdrop-blur-xl border border-white/5 overflow-hidden shadow-2xl shadow-black/50 flex flex-col w-full h-full xl:col-span-1">
                                            <div className="h-1 bg-gradient-to-r from-blue-500 to-cyan-500" />
                                            <div className="p-4 bg-gray-900/40 border-b border-gray-700/50 flex justify-between items-center">
                                                <h3 className="font-bold text-lg text-blue-300">
                                                    Logline
                                                </h3>
                                                <span className="text-[10px] px-2 py-1 rounded bg-gray-800 text-gray-400 border border-gray-700 font-mono uppercase">
                                                    {distillationMeta?.model || '—'}
                                                </span>
                                            </div>
                                            <div className="p-6 flex-1 bg-gray-900/20">
                                                <p className="text-gray-200 font-serif text-lg italic border-l-4 border-blue-500 pl-4 py-1 leading-relaxed">
                                                    {metaLogline}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Demographics & Comps */}
                                    <div className="rounded-[2rem] bg-gray-900/40 backdrop-blur-xl border border-white/5 overflow-hidden shadow-2xl shadow-black/50 flex flex-col w-full h-full xl:col-span-2">
                                        <div className="h-1 bg-gradient-to-r from-orange-500 to-pink-500" />
                                        <div className="p-4 bg-gray-900/40 border-b border-gray-700/50 flex justify-between items-center">
                                            <h3 className="font-bold text-lg text-orange-300">
                                                Demographics & Comps
                                            </h3>
                                            <span className="text-[10px] px-2 py-1 rounded bg-gray-800 text-gray-400 border border-gray-700 font-mono uppercase">
                                                {distillationMeta?.model || '—'}
                                            </span>
                                        </div>
                                        <div className="p-6 flex flex-col gap-8 h-full bg-gray-900/20">
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                <div>
                                                    <h4 className="text-[10px] font-bold text-orange-400 uppercase tracking-wider mb-1">Age Range</h4>
                                                    <p className="text-gray-200 text-sm font-medium">{metaDemographics?.age_range || '-'}</p>
                                                </div>
                                                <div>
                                                    <h4 className="text-[10px] font-bold text-orange-400 uppercase tracking-wider mb-1">Gender</h4>
                                                    <p className="text-gray-200 text-sm font-medium">{metaDemographics?.gender || '-'}</p>
                                                </div>
                                                <div>
                                                    <h4 className="text-[10px] font-bold text-orange-400 uppercase tracking-wider mb-1">Status</h4>
                                                    <p className="text-gray-200 text-sm font-medium">{metaDemographics?.socioeconomic_status || '-'}</p>
                                                </div>
                                                <div>
                                                    <h4 className="text-[10px] font-bold text-orange-400 uppercase tracking-wider mb-1">Habits</h4>
                                                    <p className="text-gray-200 text-sm font-medium">{metaDemographics?.viewing_habits || '-'}</p>
                                                </div>
                                            </div>
                                            <div>
                                                <h4 className="text-[10px] font-bold text-orange-400 uppercase tracking-wider mb-1">Interests</h4>
                                                <p className="text-gray-200 text-sm font-medium">{metaDemographics?.interests || '-'}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Script Info */}
                                    {Object.keys(metaScriptInfo || {}).length > 0 && (
                                        <div className="rounded-[2rem] bg-gray-900/40 backdrop-blur-xl border border-white/5 overflow-hidden shadow-2xl shadow-black/50 flex flex-col w-full xl:col-span-3">
                                            <div className="h-1 bg-gradient-to-r from-emerald-500 to-teal-500" />
                                            <div className="p-4 bg-gray-900/40 border-b border-gray-700/50 flex justify-between items-center">
                                                <h3 className="font-bold text-lg text-emerald-300">
                                                    Title Page & Structural Data
                                                </h3>
                                            </div>
                                            <div className="p-6 bg-gray-900/20 grid grid-cols-2 lg:grid-cols-4 gap-4">
                                                {Object.entries(metaScriptInfo).map(([key, val], idx) => (
                                                    <div key={idx} className="bg-gray-800/80 p-3 rounded-lg border border-gray-700/50">
                                                        <span className="text-[10px] text-gray-400 font-bold uppercase block mb-1">{key}</span>
                                                        <span className="text-sm text-gray-200 font-medium whitespace-pre-wrap">{val || '-'}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Comps (Now handle dictionary values) */}
                                    <div className="rounded-[2rem] bg-gray-900/40 backdrop-blur-xl border border-white/5 overflow-hidden shadow-2xl shadow-black/50 flex flex-col w-full xl:col-span-3">
                                        <div className="p-6 bg-gray-900/20">
                                            <h4 className="text-xs font-bold text-pink-400 uppercase tracking-wider mb-3">Comparable Titles</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                <div className="bg-gray-800/80 p-4 rounded-xl border border-gray-700/50">
                                                    <span className="text-[10px] text-gray-400 font-bold uppercase block mb-2">Films</span>
                                                    <ul className="list-disc pl-4 text-sm text-gray-300 space-y-2">
                                                        {(metaComps?.films || []).map((t, idx) => (
                                                            <li key={idx}>
                                                                <span className="font-bold text-white">{typeof t === 'string' ? t : t.title}</span>
                                                                {typeof t === 'object' && t.year && <span className="text-gray-400 ml-1">({t.year})</span>}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                                <div className="bg-gray-800/80 p-4 rounded-xl border border-gray-700/50">
                                                    <span className="text-[10px] text-gray-400 font-bold uppercase block mb-2">Series</span>
                                                    <ul className="list-disc pl-4 text-sm text-gray-300 space-y-2">
                                                        {(metaComps?.series || []).map((t, idx) => (
                                                            <li key={idx}>
                                                                <span className="font-bold text-white">{typeof t === 'string' ? t : t.title}</span>
                                                                {typeof t === 'object' && t.year && <span className="text-gray-400 ml-1">({t.year})</span>}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                                <div className="bg-gray-800/80 p-4 rounded-xl border border-gray-700/50">
                                                    <span className="text-[10px] text-gray-400 font-bold uppercase block mb-2">Books</span>
                                                    <ul className="list-disc pl-4 text-sm text-gray-300 space-y-2">
                                                        {(metaComps?.books || []).map((t, idx) => (
                                                            <li key={idx}>
                                                                <span className="font-bold text-white">{typeof t === 'string' ? t : t.title}</span>
                                                                {typeof t === 'object' && t.year && <span className="text-gray-400 ml-1">({t.year})</span>}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Adaptability */}
                                    {metaAdaptability && (
                                        <div className="rounded-[2rem] bg-gray-900/40 backdrop-blur-xl border border-white/5 overflow-hidden shadow-2xl shadow-black/50 flex flex-col w-full xl:col-span-3">
                                            <div className="h-1 bg-gradient-to-r from-violet-500 to-purple-500" />
                                            <div className="p-4 bg-gray-900/40 border-b border-gray-700/50 flex justify-between items-center">
                                                <h3 className="font-bold text-lg text-violet-300">
                                                    Adaptability Analysis
                                                </h3>
                                                <span className="text-[10px] px-2 py-1 rounded bg-gray-800 text-gray-400 border border-gray-700 font-mono uppercase">
                                                    {adaptabilityMeta?.model || '—'}
                                                </span>
                                            </div>
                                            <div className="p-6 bg-gray-900/20">
                                                <p className="text-gray-300 font-serif leading-relaxed text-sm whitespace-pre-wrap">
                                                    {metaAdaptability}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                </div>
                            )}

                            {/* Character Breakdowns Output */}
                            {jobId && metaCharacters && metaCharacters.length > 0 && (
                                <div>
                                    <div className="rounded-[2rem] bg-gray-900/40 backdrop-blur-xl border border-white/5 overflow-hidden shadow-2xl shadow-black/50 flex flex-col w-full">
                                        <div className="h-1 bg-gradient-to-r from-yellow-500 to-amber-500" />
                                        <div className="p-4 bg-gray-900/40 border-b border-gray-700/50 flex justify-between items-center">
                                            <h3 className="font-bold text-lg text-yellow-500">
                                                Character Breakdowns ({metaCharacters.length})
                                            </h3>
                                            <span className="text-[10px] px-2 py-1 rounded bg-gray-800 text-gray-400 border border-gray-700 font-mono uppercase">
                                                {characterMeta?.model || '—'}
                                            </span>
                                        </div>
                                        <div className="p-6 bg-gray-900/20 grid grid-cols-1 lg:grid-cols-2 gap-6">
                                            {metaCharacters.map((char, index) => (
                                                <div key={index} className="bg-gray-800/80 rounded-xl p-5 border border-gray-700/50 hover:border-gray-500 transition-colors flex flex-col xl:flex-row gap-5">
                                                    {char.image_url && (
                                                        <div className="xl:w-1/3 flex-shrink-0">
                                                            <img src={char.image_url} alt={char.name} className="w-full h-auto rounded-lg shadow-md border border-gray-700 object-cover" />
                                                        </div>
                                                    )}
                                                    <div className={char.image_url ? "xl:w-2/3" : "w-full"}>
                                                        <div className="flex items-end justify-between mb-3 border-b border-gray-700 pb-2 gap-2">
                                                            <h4 className="text-lg font-bold text-gray-100">{char.name}</h4>
                                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => startCharacterDeepDive(index)}
                                                                    disabled={deepDiveLoading && deepDiveCharIndex === index}
                                                                    className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md bg-amber-600/80 hover:bg-amber-500 text-white border border-amber-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                                >
                                                                    {deepDiveLoading && deepDiveCharIndex === index ? 'Running…' : 'Deep Dive'}
                                                                </button>
                                                                <span className="text-[10px] font-mono text-gray-400 uppercase font-bold tracking-wider">
                                                                    {char.gender} • {char.age_range}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="text-sm text-gray-300 mb-4 leading-relaxed bg-gray-900/60 p-3 rounded-lg">
                                                            <span className="text-[10px] font-bold text-blue-400 uppercase tracking-wider block mb-1">Description</span>
                                                            {char.description}
                                                        </div>
                                                        <div>
                                                            <span className="text-[10px] font-bold text-amber-500 uppercase tracking-wider block mb-1">Narrative Arc</span>
                                                            <p className="text-sm text-gray-400 font-serif italic">
                                                                {char.character_arc}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ── Run Summary Observability Panel ── */}
                            {jobId && (chunks.length > 0 || finalMeta) && (
                                (() => {
                                    // Parse model/attempts out of the prompt_input header that tasks.py prepends
                                    const parseChunkMeta = (chunk) => {
                                        const pi = chunk.prompt_input || '';
                                        const model = pi.match(/^MODEL_USED:\s*(.+)$/m)?.[1]?.trim() || '—';
                                        const attMatch = pi.match(/across (\d+) attempt/);
                                        const attempts = attMatch ? parseInt(attMatch[1]) : 1;
                                        return { model, attempts };
                                    };

                                    const fmt$ = (n) => `$${(n || 0).toFixed(4)}`;
                                    const fmtTok = (n) => (n || 0).toLocaleString();

                                    const chunkTotalTokens = chunks.reduce((s, c) => s + (c.tokens || 0), 0);
                                    const chunkTotalCost = chunks.reduce((s, c) => s + (c.cost || 0), 0);
                                    const grandTokens = chunkTotalTokens + (finalMeta?.tokens || 0) + (shortMeta?.tokens || 0);
                                    const grandCost = chunkTotalCost + (finalMeta?.cost || 0) + (shortMeta?.cost || 0) + (distillationMeta?.cost || 0) + (characterMeta?.cost || 0) + (imageMeta?.cost || 0) + (adaptabilityMeta?.cost || 0);

                                    const outcomeColor = (outcome) => {
                                        if (!outcome || outcome === 'success') return 'text-green-400';
                                        if (outcome === 'best_effort') return 'text-amber-400';
                                        if (['refusal', 'content_filter', 'empty_response'].includes(outcome)) return 'text-red-400';
                                        return 'text-yellow-400';
                                    };

                                    return (
                                        <div className="mt-6">
                                            <div className="bg-gray-900 rounded-lg border border-gray-700 shadow-xl overflow-hidden">
                                                <div className="px-4 py-3 bg-gray-800 border-b border-gray-700 flex items-center gap-3">
                                                    <h3 className="font-bold text-white text-sm uppercase tracking-wider">Run Summary</h3>
                                                    <span className="text-xs text-gray-400 font-mono">{fmtTok(grandTokens)} tokens · {fmt$(grandCost)} total</span>
                                                </div>

                                                <div className="overflow-x-auto">
                                                    <table className="w-full text-xs font-mono">
                                                        <thead>
                                                            <tr className="text-gray-400 border-b border-gray-700 bg-gray-850">
                                                                <th className="text-left px-4 py-2">Step</th>
                                                                <th className="text-left px-4 py-2">Status</th>
                                                                <th className="text-left px-4 py-2">Final Model</th>
                                                                <th className="text-right px-4 py-2">Attempts</th>
                                                                <th className="text-right px-4 py-2">Tokens</th>
                                                                <th className="text-right px-4 py-2">Cost (incl. retries)</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-gray-800">
                                                            {/* Script Input Row (Replaces chunks map loop) */}
                                                            <tr className="hover:bg-gray-800/50 transition-colors">
                                                                <td className="px-4 py-2 text-gray-300">Script Payload</td>
                                                                <td className="px-4 py-2">
                                                                    <span className="px-2 py-0.5 rounded text-xs font-bold bg-green-900/50 text-green-300">LOADED</span>
                                                                </td>
                                                                <td className="px-4 py-2 text-gray-500">—</td>
                                                                <td className="px-4 py-2 text-right text-gray-500">—</td>
                                                                <td className="px-4 py-2 text-right text-gray-500">—</td>
                                                                <td className="px-4 py-2 text-right text-gray-500">—</td>
                                                            </tr>

                                                            {/* Final synopsis row */}
                                                            {finalMeta && (
                                                                <React.Fragment>
                                                                    <tr className="hover:bg-gray-800/50 transition-colors border-t border-gray-600">
                                                                        <td className="px-4 py-2 text-blue-300 font-bold">Final Synopsis</td>
                                                                        <td className="px-4 py-2">
                                                                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                                                                finalSynopsisStatus === 'COMPLETED' ? 'bg-green-900/50 text-green-300' :
                                                                                finalSynopsisStatus === 'FAILED' ? 'bg-red-900/50 text-red-300' :
                                                                                'bg-yellow-900/50 text-yellow-300'
                                                                            }`}>{finalSynopsisStatus || 'PENDING'}</span>
                                                                        </td>
                                                                        <td className="px-4 py-2 text-gray-300">{finalMeta.model || '—'}</td>
                                                                        <td className="px-4 py-2 text-right">
                                                                            {(finalMeta.attempts || 0) > 1
                                                                                ? <span className="text-amber-400 font-bold">{finalMeta.attempts}</span>
                                                                                : <span className="text-gray-400">{finalMeta.attempts || '—'}</span>
                                                                            }
                                                                        </td>
                                                                        <td className="px-4 py-2 text-right text-gray-300">{fmtTok(finalMeta.tokens)}</td>
                                                                        <td className="px-4 py-2 text-right text-gray-300">{fmt$(finalMeta.cost)}</td>
                                                                    </tr>
                                                                </React.Fragment>
                                                            )}
                                                            
                                                            {/* Short synopsis row */}
                                                            {shortMeta && (
                                                                <React.Fragment>
                                                                    <tr className="hover:bg-gray-800/50 transition-colors border-t border-gray-600">
                                                                        <td className="px-4 py-2 text-green-300 font-bold">Short Synopsis</td>
                                                                        <td className="px-4 py-2">
                                                                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                                                                jobStatus === 'COMPLETED' ? 'bg-green-900/50 text-green-300' :
                                                                                jobStatus === 'FAILED' ? 'bg-red-900/50 text-red-300' :
                                                                                'bg-yellow-900/50 text-yellow-300'
                                                                            }`}>{jobStatus === 'COMPLETED' ? 'COMPLETED' : jobStatus === 'FAILED' ? 'FAILED' : 'PENDING'}</span>
                                                                        </td>
                                                                        <td className="px-4 py-2 text-gray-300">{shortMeta.model || '—'}</td>
                                                                        <td className="px-4 py-2 text-right">
                                                                            {(shortMeta.attempts || 0) > 1
                                                                                ? <span className="text-amber-400 font-bold">{shortMeta.attempts}</span>
                                                                                : <span className="text-gray-400">{shortMeta.attempts || '—'}</span>
                                                                            }
                                                                        </td>
                                                                        <td className="px-4 py-2 text-right text-gray-300">{fmtTok(shortMeta.tokens)}</td>
                                                                        <td className="px-4 py-2 text-right text-gray-300">{fmt$(shortMeta.cost)}</td>
                                                                    </tr>
                                                                    {/* Final synopsis per-attempt breakdown */}
                                                                    {(finalMeta.attempt_log || []).length > 1 && (finalMeta.attempt_log || []).map((att, ai) => {
                                                                        const isUsed = att.outcome === 'success' || att.outcome === 'best_effort';
                                                                        return (
                                                                            <tr key={`final-att-${ai}`} className={`border-l-2 ${isUsed ? 'bg-green-950/40 border-green-600' : 'bg-gray-950 border-blue-700/40'}`}>
                                                                                <td className={`pl-8 pr-4 py-1 ${isUsed ? 'text-green-300 font-bold' : 'text-gray-500'}`}>
                                                                                    ↳ attempt {att.attempt}{isUsed ? ' ✓ used' : ''}
                                                                                </td>
                                                                                <td className="px-4 py-1">
                                                                                    <span className={`text-xs ${outcomeColor(att.outcome)}`}>{att.outcome}</span>
                                                                                </td>
                                                                                <td className={`px-4 py-1 ${isUsed ? 'text-green-300' : 'text-gray-500'}`}>{att.model}</td>
                                                                                <td className="px-4 py-1 text-right text-gray-500">—</td>
                                                                                <td className={`px-4 py-1 text-right ${isUsed ? 'text-green-300' : 'text-gray-500'}`}>{fmtTok(att.tokens)}</td>
                                                                                <td className={`px-4 py-1 text-right ${isUsed ? 'text-green-300' : 'text-gray-500'}`}>{fmt$(att.cost)}</td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </React.Fragment>
                                                            )}

                                                            {/* Distillation row */}
                                                            {distillationMeta && (
                                                                <tr className="hover:bg-gray-800/50 transition-colors border-t border-gray-600">
                                                                    <td className="px-4 py-2 text-cyan-300 font-bold">Meta Distillation</td>
                                                                    <td className="px-4 py-2">
                                                                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                                                            jobStatus === 'COMPLETED' ? 'bg-green-900/50 text-green-300' :
                                                                            jobStatus === 'FAILED' ? 'bg-red-900/50 text-red-300' :
                                                                            'bg-yellow-900/50 text-yellow-300'
                                                                        }`}>{jobStatus === 'COMPLETED' ? (metaLogline ? 'COMPLETED' : 'FAILED') : 'PENDING'}</span>
                                                                    </td>
                                                                    <td className="px-4 py-2 text-gray-300">{distillationMeta.model || '—'}</td>
                                                                    <td className="px-4 py-2 text-right"><span className="text-gray-400">1</span></td>
                                                                    <td className="px-4 py-2 text-right text-gray-300">—</td>
                                                                    <td className="px-4 py-2 text-right text-gray-300">{fmt$(distillationMeta.cost)}</td>
                                                                </tr>
                                                            )}

                                                            {/* Character Extraction row */}
                                                            {characterMeta && (
                                                                <tr className="hover:bg-gray-800/50 transition-colors border-t border-gray-600">
                                                                    <td className="px-4 py-2 text-amber-300 font-bold">Character Extract</td>
                                                                    <td className="px-4 py-2">
                                                                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                                                            jobStatus === 'COMPLETED' ? 'bg-green-900/50 text-green-300' :
                                                                            jobStatus === 'FAILED' ? 'bg-red-900/50 text-red-300' :
                                                                            'bg-yellow-900/50 text-yellow-300'
                                                                        }`}>{jobStatus === 'COMPLETED' ? (metaCharacters.length > 0 ? 'COMPLETED' : 'FAILED') : 'PENDING'}</span>
                                                                    </td>
                                                                    <td className="px-4 py-2 text-gray-300">{characterMeta.model || '—'}</td>
                                                                    <td className="px-4 py-2 text-right"><span className="text-gray-400">1</span></td>
                                                                    <td className="px-4 py-2 text-right text-gray-300">—</td>
                                                                    <td className="px-4 py-2 text-right text-gray-300">{fmt$(characterMeta.cost)}</td>
                                                                </tr>
                                                            )}

                                                            {/* Adaptability row */}
                                                            {adaptabilityMeta && (
                                                                <tr className="hover:bg-gray-800/50 transition-colors border-t border-gray-600">
                                                                    <td className="px-4 py-2 text-violet-300 font-bold">Adaptability</td>
                                                                    <td className="px-4 py-2">
                                                                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                                                            jobStatus === 'COMPLETED' ? 'bg-green-900/50 text-green-300' :
                                                                            jobStatus === 'FAILED' ? 'bg-red-900/50 text-red-300' :
                                                                            'bg-yellow-900/50 text-yellow-300'
                                                                        }`}>{jobStatus === 'COMPLETED' ? (metaAdaptability ? 'COMPLETED' : 'FAILED') : 'PENDING'}</span>
                                                                    </td>
                                                                    <td className="px-4 py-2 text-gray-300">{adaptabilityMeta.model || '—'}</td>
                                                                    <td className="px-4 py-2 text-right"><span className="text-gray-400">1</span></td>
                                                                    <td className="px-4 py-2 text-right text-gray-300">—</td>
                                                                    <td className="px-4 py-2 text-right text-gray-300">{fmt$(adaptabilityMeta.cost)}</td>
                                                                </tr>
                                                            )}

                                                            {/* Image generation row */}
                                                            {imageMeta && (
                                                                <tr className="hover:bg-gray-800/50 transition-colors border-t border-gray-600">
                                                                    <td className="px-4 py-2 text-rose-300 font-bold">Character Images</td>
                                                                    <td className="px-4 py-2">
                                                                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                                                            jobStatus === 'COMPLETED' ? 'bg-green-900/50 text-green-300' :
                                                                            jobStatus === 'FAILED' ? 'bg-red-900/50 text-red-300' :
                                                                            'bg-yellow-900/50 text-yellow-300'
                                                                        }`}>{jobStatus === 'COMPLETED' ? ((metaCharacters.filter(c => c.image_url)?.length > 0) ? 'COMPLETED' : 'FAILED') : 'PENDING'}</span>
                                                                    </td>
                                                                    <td className="px-4 py-2 text-gray-300">{imageMeta.model || '—'}</td>
                                                                    <td className="px-4 py-2 text-right"><span className="text-gray-400">1</span></td>
                                                                    <td className="px-4 py-2 text-right text-gray-300">—</td>
                                                                    <td className="px-4 py-2 text-right text-gray-300">{fmt$(imageMeta.cost)}</td>
                                                                </tr>
                                                            )}

                                                            {/* Grand total row */}
                                                            <tr className="bg-gray-800 border-t-2 border-gray-600 font-bold">
                                                                <td className="px-4 py-2 text-white" colSpan={4}>TOTAL (incl. retries)</td>
                                                                <td className="px-4 py-2 text-right text-white">{fmtTok(grandTokens)}</td>
                                                                <td className="px-4 py-2 text-right text-white">{fmt$(grandCost)}</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()
                            )}
                        </div>
                    )}
                </div>
                
                <InfoModal isOpen={isInfoOpen} onClose={() => setIsInfoOpen(false)} />
        </PageContainer>
    );
};

export default LabV4;
